import { mergeOrderAfterUpdate } from './mergeOrderAfterUpdate';
import { buildQuoteFundingFields } from './quoteFundingPayload';
import { formatBudgetLineLabel } from './labData';

const SENTINEL_BUDGET_VALUES = new Set(['null', 'undefined', 'none', 'n/a']);

/**
 * Display label for quote/project budget (budget_line object or legacy text).
 *
 * @param {{ budget_line?: { code?: string, name?: string }, budget?: string }|null|undefined} quoteOrProject
 * @returns {string}
 */
export function formatQuoteBudgetDisplay(quoteOrProject) {
  if (!quoteOrProject) return '';
  const lineLabel = formatBudgetLineLabel(quoteOrProject.budget_line);
  if (lineLabel) return lineLabel;
  return normalizeBudgetText(quoteOrProject.budget);
}

/**
 * Normalize budget text from API/form (reject JSON-null serialized as string).
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeBudgetText(value) {
  if (value == null) {
    return '';
  }
  const text = String(value).trim();
  if (text === '') {
    return '';
  }
  if (SENTINEL_BUDGET_VALUES.has(text.toLowerCase())) {
    return '';
  }
  return text;
}

/**
 * Order amount charged from credit (API field name variants).
 *
 * @param {Object|null|undefined} order
 * @returns {*}
 */
export function resolveOrderAmountCharged(order) {
  if (!order) {
    return null;
  }
  const charged =
    order.amount_charged ??
    order.amount_charged_from_credit ??
    null;
  return charged != null && charged !== '' ? charged : null;
}

/**
 * Order-level supplier credit id (payment), not quote funding_credit.
 *
 * @param {Object|null|undefined} order
 * @returns {number|string|null}
 */
function resolveOrderSupplierCreditId(order) {
  if (order?.supplier_credit == null) {
    return null;
  }
  const credit = order.supplier_credit;
  if (typeof credit === 'object' && credit?.id != null) {
    return credit.id;
  }
  if (credit !== '') {
    return credit;
  }
  return null;
}

/**
 * Resolve funding credit id from a quote-like object.
 *
 * @param {Object|null|undefined} quote
 * @returns {number|string|null}
 */
function resolveQuoteFundingCreditId(quote) {
  if (!quote || typeof quote !== 'object') {
    return null;
  }
  const fundingCredit = quote.funding_credit ?? quote.supplier_credit ?? null;
  if (typeof fundingCredit === 'object' && fundingCredit?.id != null) {
    return fundingCredit.id;
  }
  if (fundingCredit != null && fundingCredit !== '') {
    return fundingCredit;
  }
  if (quote.funding_credit_id != null) {
    return quote.funding_credit_id;
  }
  if (quote.supplier_credit_id != null) {
    return quote.supplier_credit_id;
  }
  return null;
}

/**
 * Resolve quote id from an order payload.
 *
 * @param {Object|null|undefined} order
 * @returns {number|string|null}
 */
function resolveOrderQuoteId(order) {
  if (!order?.quote) {
    return null;
  }
  return typeof order.quote === 'object' ? order.quote.id : order.quote;
}

/**
 * Merge list-row order funding into get_details payload when the API omits quote.budget.
 *
 * @param {Object|null|undefined} listOrder
 * @param {Object|null|undefined} detailedOrder
 * @returns {Object|null}
 */
export function mergeOrderDetailsForDisplay(listOrder, detailedOrder) {
  if (!detailedOrder) {
    return listOrder ?? null;
  }
  if (!listOrder) {
    return detailedOrder;
  }
  return mergeOrderAfterUpdate(listOrder, detailedOrder);
}

/**
 * True when order has a linked quote but funding fields are missing from the payload.
 *
 * @param {Object|null|undefined} order
 * @returns {boolean}
 */
export function needsOrderFundingEnrichment(order) {
  if (!order) {
    return false;
  }
  if (getOrderFundingDisplay(order).type !== 'none') {
    return false;
  }
  return resolveOrderQuoteId(order) != null;
}

/**
 * Attach quote funding fields from quote get_details onto an order for display.
 *
 * @param {Object} order
 * @param {Object} quoteDetails
 * @returns {Object}
 */
export function enrichOrderWithQuoteFunding(order, quoteDetails) {
  if (!order || !quoteDetails) {
    return order;
  }
  const quoteId = resolveOrderQuoteId(order) ?? quoteDetails.id;
  const existingQuote =
    typeof order.quote === 'object' && order.quote != null
      ? order.quote
      : { id: quoteId };
  return {
    ...order,
    quote: {
      ...existingQuote,
      ...quoteDetails,
      id: quoteId ?? quoteDetails.id,
    },
  };
}

/**
 * Resolve quote object from an order (nested or absent).
 *
 * @param {Object|null|undefined} order
 * @returns {Object|null}
 */
function resolveOrderQuote(order) {
  if (!order?.quote) {
    return null;
  }
  return typeof order.quote === 'object' ? order.quote : null;
}

/**
 * Funding display classification for orders (list, detail modal, tables).
 *
 * @param {Object|null|undefined} order - Order with optional nested quote.
 * @returns {{
 *   type: 'order_credit' | 'quote_credit' | 'budget' | 'prepayment' | 'none',
 *   credit?: *,
 *   creditId?: number|string|null,
 *   amountCharged?: *,
 *   budget?: string,
 *   prepaymentAmount?: *,
 *   prepaymentCurrency?: string|null,
 * }}
 */
export function getOrderFundingDisplay(order) {
  if (!order) {
    return { type: 'none' };
  }

  const quote = resolveOrderQuote(order);

  if (quote) {
    const lineLabel = formatBudgetLineLabel(quote.budget_line);
    if (lineLabel) {
      return { type: 'budget', budget: lineLabel, budgetLine: quote.budget_line };
    }

    const budget = normalizeBudgetText(quote.budget);
    if (budget !== '') {
      return { type: 'budget', budget };
    }

    const quoteCreditId = resolveQuoteFundingCreditId(quote);
    if (quoteCreditId != null) {
      const credit = quote.funding_credit ?? quote.supplier_credit ?? null;
      return {
        type: 'quote_credit',
        credit,
        creditId: quoteCreditId,
      };
    }

    if (quote.prepayment_amount != null && quote.prepayment_amount !== '') {
      return {
        type: 'prepayment',
        prepaymentAmount: quote.prepayment_amount,
        prepaymentCurrency: quote.prepayment_currency ?? null,
      };
    }
  }

  if (order.supplier_credit != null) {
    return {
      type: 'order_credit',
      credit: order.supplier_credit,
      creditId:
        typeof order.supplier_credit === 'object'
          ? order.supplier_credit.id
          : order.supplier_credit,
      amountCharged: resolveOrderAmountCharged(order),
    };
  }

  return { type: 'none' };
}

/**
 * Whether OrderModal may show the quote funding editor.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} [_relatedQuote]
 * @param {{ isPrepayment?: boolean }} [options]
 * @returns {boolean}
 */
export function isOrderQuoteFundingEditable(
  order,
  _relatedQuote = null,
  { isPrepayment = false } = {}
) {
  return Boolean(order) && !isPrepayment;
}

/**
 * Initial budget id for OrderModal edit from nested budget_line / budget_id.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} [relatedQuote]
 * @returns {string}
 */
export function getOrderEditBudgetIdInitialValue(order, relatedQuote = null) {
  const quote = relatedQuote ?? resolveOrderQuote(order);
  const nestedId = quote?.budget_line?.id ?? quote?.budget_id ?? null;
  return nestedId != null ? String(nestedId) : '';
}

/**
 * Initial budget text for OrderModal edit from order + optional loaded quote.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} [relatedQuote]
 * @returns {string}
 */
export function getOrderEditBudgetInitialValue(order, relatedQuote = null) {
  const quote = relatedQuote ?? resolveOrderQuote(order);
  const lineLabel = formatBudgetLineLabel(quote?.budget_line);
  if (lineLabel) return lineLabel;
  return normalizeBudgetText(quote?.budget);
}

/**
 * Initial supplier credit id for OrderModal edit (quote funding, then order payment credit).
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} [relatedQuote]
 * @returns {string}
 */
export function getOrderEditFundingCreditIdInitialValue(order, relatedQuote = null) {
  const quote = relatedQuote ?? resolveOrderQuote(order);
  const fromQuote = resolveQuoteFundingCreditId(quote);
  if (fromQuote != null) {
    return String(fromQuote);
  }
  const fromOrder = resolveOrderSupplierCreditId(order);
  return fromOrder != null ? String(fromOrder) : '';
}

/**
 * Initial funding source type for OrderModal edit.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} [relatedQuote]
 * @returns {'budget'|'credit'}
 */
export function getOrderEditFundingSourceTypeInitialValue(order, relatedQuote = null) {
  if (getOrderEditBudgetIdInitialValue(order, relatedQuote)) {
    return 'budget';
  }
  if (getOrderEditFundingCreditIdInitialValue(order, relatedQuote)) {
    return 'credit';
  }
  return 'budget';
}

/**
 * Line items for funding_amount when quote detail items are missing.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} relatedQuote
 * @returns {Array<{ quantity: *, price: *, discount: * }>}
 */
export function resolveFundingItemsForOrderPatch(order, relatedQuote) {
  const quoteItems = relatedQuote?.items;
  if (Array.isArray(quoteItems) && quoteItems.length > 0) {
    return quoteItems.map(item => ({
      quantity: item.quantity,
      price: item.price,
      discount: item.discount,
    }));
  }

  const orderItems = order?.items;
  if (!Array.isArray(orderItems) || orderItems.length === 0) {
    return [];
  }

  return orderItems.map(item => ({
    quantity: item.quote_item?.quantity ?? item.quantity ?? 1,
    price: item.product?.price ?? item.price,
    discount: item.discount ?? '',
  }));
}

/**
 * Build quote funding fields for PATCH /orders/ when editing an order.
 *
 * @param {Object|null|undefined} order
 * @param {Object|null|undefined} relatedQuote
 * @param {{ budgetId?: string|number|null, budget?: string, fundingCreditId?: string }} formValues
 * @returns {{ ok: true, fields: Object } | { ok: false, error: string }}
 */
export function buildOrderQuoteFundingPatchFields(
  order,
  relatedQuote,
  formValues = {}
) {
  if (!order) {
    return { ok: true, fields: {} };
  }

  const items = resolveFundingItemsForOrderPatch(order, relatedQuote);

  return buildQuoteFundingFields({
    budgetId: formValues.budgetId,
    budget: normalizeBudgetText(formValues.budget),
    fundingCreditId: formValues.fundingCreditId,
    items,
  });
}
